import type { PayloadAction } from "@reduxjs/toolkit";

import type { UpdateEvents } from "../../types/event-updates";
import type { State, setStatePayload } from "./kiosk-types";

export const setKioskStageHandler = (
	state: State,
	payload: PayloadAction<setStatePayload>,
) => {
	const oldSessionIdx = state.sessionIdx;

	// Direct mutation is allowed in Redux Toolkit (Immer)
	state.stage = payload.payload.stage;
	state.subStage = payload.payload.subStage ?? state.subStage;
	if (payload.payload.sessionIdx !== undefined) {
		state.sessionIdx = payload.payload.sessionIdx;
	}

	// Clear playback queue if focus switched to a new session.
	// This prevents replays from the previous game from leaking into the next.
	if (oldSessionIdx !== undefined && state.sessionIdx !== oldSessionIdx) {
		state.playbackQueue = [];
		state.currentPlayback = null;
	}
};

export const kioskQueuePlaybackHandler = (
	state: State,
	{ payload }: PayloadAction<any>,
) => {
	// Bug fix: only accept replays related to the currently focused session
	// unless in 'idle' stage where we show overall event content.
	const isIdle = state.stage === "idle";
	if (!isIdle && payload.session_idx !== state.sessionIdx) {
		return;
	}

	if (!state.currentPlayback) {
		state.currentPlayback = payload;
	} else {
		state.playbackQueue.push(payload);
	}
};

export const kioskBatchQueuePlaybacksHandler = (
	state: State,
	{ payload }: PayloadAction<any[]>,
) => {
	for (const item of payload) {
		if (!state.currentPlayback) {
			state.currentPlayback = item;
		} else {
			state.playbackQueue.push(item);
		}
	}
};

export const kioskDequeuePlaybackHandler = (state: State) => {
	if (state.playbackQueue.length > 0) {
		state.currentPlayback = state.playbackQueue.shift();
	} else {
		state.currentPlayback = null;
	}
};

export const updatePlaylistHandler = (
	state: State,
	{ payload }: PayloadAction<{ playlist: string[]; resetIndex?: boolean }>,
) => {
	state.playlist = payload.playlist;
	if (payload.resetIndex || state.playlistIdx >= payload.playlist.length) {
		state.playlistIdx = 0;
	}
};

export const advancePlaylistHandler = (state: State) => {
	if (state.playlist.length > 0) {
		state.playlistIdx = (state.playlistIdx + 1) % state.playlist.length;
	}
};

// NOTICE: this should be attached inside a single sse update handler
export const proxyKioskUpdateHandler = (
	state: State,
	{ payload: update }: PayloadAction<UpdateEvents>,
) => {
	if ("EventUpdate" in update) {
		const e = update.EventUpdate;

		if (typeof e === "string") {
			if (e === "started") {
				state.stage = "playing";
				state.subStage = "regular";
			} else if (e === "finished") {
				state.stage = "endgame";
				state.subStage = "processing";
			} else if (e === "cancelled") {
				state.stage = "idle";
			}
		} else {
			if ("booked" in e) {
			} else if ("session_appended" in e) {
				// Heartbeat-driven determineEventState is the source of truth for sessionIdx focus.
				// We avoid updating state.sessionIdx here to prevent premature focus shifts
				// while the previous session might still be in endgame/stats.
			} else if ("session_ready" in e) {
				// Force transition to waiting if we weren't playing.
				// We keep the stage change but omit sessionIdx update here as well.
				if (state.stage !== "playing") {
					state.stage = "waiting";
					state.subStage = "normal";
				}
			}
		}
	} else {
		const e = update.SessionUpdate;

		if ("processing_done" in e) {
			state.processed = true;
			if (state.stage === "endgame") {
				state.subStage = "all_processed";
			}
		} else if ("session_archived" in e) {
			state.archived = true;
			state.processed = true;
			if (state.stage === "endgame") {
				state.subStage = "all_processed";
			}
		} else if ("replay_ready" in e) {
			// Logic for ephemeral states like new_shot could be here
			// But usually this is handled by the UI reacting to the new replay in the queue
			// state.subStage = "new_shot"; // Optional: if we want to enforce layout change
		}
	}
};
